import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { StudentCreatePayload, StudentSummary } from '../../../lib/classroomsApi';
import { api } from '../../../lib/api';
import { calculateOver16 } from '../utils';
import CatalogPanel from './CatalogPanel';
import TemePanel from './TemePanel';
import CursuriPanel from './CursuriPanel';
import OrarPanel from './OrarPanel';
import ProfesoriPanel from './ProfesoriPanel';
import { parseApiError } from '../utils';

type CatalogGradeLite = {
  student_id: number;
  value: number | null;
  qualifier: string | null;
  date: string | null;
  created_at?: string | null;
  is_conduct?: boolean;
};

type CatalogAbsenceLite = { student_id: number; total: number; motivated: number };

type GradebookConfigLite = {
  id: number;
  education_level?: string | null;
};

type CatalogOverviewLite = {
  config: { education_level?: string | null };
  students: { id: number }[];
  grades: CatalogGradeLite[];
  absences: CatalogAbsenceLite[];
};

type EvolutionStatus = 'bine' | 'binisor' | 'avertisment' | 'fara_date';
type EvolutionTrend = 'up' | 'down' | 'flat';
type StudentMetric = {
  status: EvolutionStatus;
  trend: EvolutionTrend;
  avg: number | null;
  absTotal: number;
  absMotivated: number;
  rating: number | null; // 0..100
};

function gradeToNumberForIndicator(g: Pick<CatalogGradeLite, 'value' | 'qualifier'>, educationLevel: string): number | null {
  if (typeof g.value === 'number') return g.value;
  const lvl = (educationLevel || '').toString().trim().toLowerCase();
  if (lvl !== 'primar') return null;
  const q = (g.qualifier || '').toString().trim().toUpperCase();
  if (!q) return null;
  if (q === 'FB') return 10;
  if (q === 'B') return 8;
  if (q === 'S') return 6;
  if (q === 'I') return 4;
  return null;
}

function avgOf(nums: number[]): number | null {
  if (!nums.length) return null;
  const a = nums.reduce((s, v) => s + v, 0) / nums.length;
  return Math.round(a * 100) / 100;
}

function formatDateOnlyRo(iso: string | null | undefined): string | null {
  const s = (iso || '').toString().trim();
  if (!s) return null;
  const parts = s.split('-').map((p) => p.trim());
  if (parts.length >= 3 && parts[0].length === 4) {
    const y = Number(parts[0]);
    const m = Number(parts[1]);
    const d = Number(parts[2].slice(0, 2));
    if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
      const dt = new Date(y, m - 1, d);
      if (!Number.isNaN(dt.getTime())) return dt.toLocaleDateString('ro-RO');
    }
  }
  const dt = new Date(s);
  if (Number.isNaN(dt.getTime())) return s;
  return dt.toLocaleDateString('ro-RO');
}

function clamp(min: number, v: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

interface Props {
  classId: number;
  className: string;
  profesor: string;
  isAdmin: boolean;
  canEditStudents?: boolean;
  isB2B?: boolean;
  activeTab: 'catalog' | 'teme' | 'cursuri' | 'students' | 'orar' | 'profesori';
  onTabChange: (tab: 'catalog' | 'teme' | 'cursuri' | 'students' | 'orar' | 'profesori') => void;
  students: StudentSummary[];
  studentsLoading: boolean;
  studentsError: string | null;
  info: string | null;
  onBack: () => void;
  onRefreshStudents: () => Promise<void>;
  onDeleteStudent: (studentId: number) => Promise<void>;
  onAddStudent: (payload: StudentCreatePayload) => Promise<void>;
  onRename?: () => void;
  onDeleteClass?: () => void;
  onAddAdmin?: () => void;
}

const ClassroomDetailView: React.FC<Props> = ({
  classId,
  className,
  profesor,
  isAdmin,
  canEditStudents,
  isB2B,
  activeTab,
  onTabChange,
  students,
  studentsLoading,
  studentsError,
  info,
  onBack,
  onRefreshStudents,
  onDeleteStudent,
  onAddStudent,
  onRename,
  onDeleteClass,
  onAddAdmin,
}) => {
  const navigate = useNavigate();
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openStudentId, setOpenStudentId] = useState<number | null>(null);
  const [showEditStudentModal, setShowEditStudentModal] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editBusy, setEditBusy] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [editStudentId, setEditStudentId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    name: '',
    birthDate: '' as string,
    manualOver16: true,
    studentEmail: '',
    studentPhone: '',
    studentAddress: '',
    parentName1: '',
    parentEmail1: '',
    parentPhone1: '',
    parentName2: '',
    parentEmail2: '',
    parentPhone2: '',
  });

  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [metricsByStudentId, setMetricsByStudentId] = useState<Record<number, StudentMetric>>({});

  const [studentForm, setStudentForm] = useState({
    name: '',
    birthDate: '' as string,
    manualOver16: true,
    studentEmail: '',
    studentPhone: '',
    studentAddress: '',
    parentName1: '',
    parentEmail1: '',
    parentPhone1: '',
    parentName2: '',
    parentEmail2: '',
    parentPhone2: '',
  });

  const goToDm = (userId: number, name: string, role: 'student' | 'parent' | 'teacher' | 'admin') => {
    const params = new URLSearchParams({
      dm: String(userId),
      name: (name || '').toString(),
      role,
    });
    navigate(`/communication-center?${params.toString()}`);
  };

  const isOver16 = useMemo(
    () => calculateOver16(studentForm.birthDate || null, studentForm.manualOver16),
    [studentForm.birthDate, studentForm.manualOver16]
  );

  const isEditOver16 = useMemo(
    () => calculateOver16(editForm.birthDate || null, editForm.manualOver16),
    [editForm.birthDate, editForm.manualOver16]
  );

  const openEditStudent = (s: StudentSummary) => {
    if (!canEditStudents) return;
    setEditError(null);
    setEditStudentId(s.id);
    setEditForm({
      name: (s.full_name || '').toString(),
      birthDate: (s.date_of_birth || '').toString(),
      manualOver16: false,
      studentEmail: (s.email || '').toString(),
      studentPhone: (s.phone || '').toString(),
      studentAddress: (s.address || '').toString(),
      parentName1: (s.parent_name1 || '').toString(),
      parentEmail1: (s.parent_email1 || '').toString(),
      parentPhone1: (s.parent_phone1 || '').toString(),
      parentName2: (s.parent_name2 || '').toString(),
      parentEmail2: (s.parent_email2 || '').toString(),
      parentPhone2: (s.parent_phone2 || '').toString(),
    });
    setShowEditStudentModal(true);
  };

  const submitEditStudent = async () => {
    if (!canEditStudents) return;
    if (!editStudentId) return;
    const name = editForm.name.trim();
    if (!name) {
      setEditError('Completeaza numele elevului.');
      return;
    }

    setEditBusy(true);
    setEditError(null);
    try {
      const over16 = isEditOver16;
      const payload = {
        name,
        birth_date: editForm.birthDate ? editForm.birthDate : null,
        over16,
        // Pentru elevii sub 16 ani, email-ul elevului este intern (ex: auto-...@student.local) si nu trebuie trimis la update.
        student_email: over16 ? editForm.studentEmail.trim() || null : null,
        student_phone: editForm.studentPhone.trim() || null,
        student_address: editForm.studentAddress.trim() || null,
        parent_name1: editForm.parentName1.trim() || null,
        parent_email1: editForm.parentEmail1.trim() || null,
        parent_phone1: editForm.parentPhone1.trim() || null,
        parent_name2: editForm.parentName2.trim() || null,
        parent_email2: editForm.parentEmail2.trim() || null,
        parent_phone2: editForm.parentPhone2.trim() || null,
      };

      const resp = await api.patch(`/api/classrooms/${classId}/students/${editStudentId}`, payload);

      const created = Array.isArray(resp.data?.created_parent_accounts) ? resp.data.created_parent_accounts : [];
      if (created.length) {
        window.alert(
          `Conturi parinti create:\n` +
            created.map((x: any) => `${x.email}: ${x.password}`).join('\n')
        );
      }

      setShowEditStudentModal(false);
      setEditStudentId(null);
      await onRefreshStudents();
    } catch (err: any) {
      setEditError(parseApiError(err));
    } finally {
      setEditBusy(false);
    }
  };

  const inviteOver16Student = async () => {
    if (!editStudentId) return;
    if (!isEditOver16) {
      setEditError('Invitatia se poate trimite doar pentru elevii de 16+.');
      return;
    }
    const email = (editForm.studentEmail || '').trim();
    if (!email) {
      setEditError('Completeaza emailul elevului (16+).');
      return;
    }

    setInviteBusy(true);
    setEditError(null);
    try {
      await api.post(`/api/classrooms/${classId}/students/${editStudentId}/invite-over16`, {
        student_email: email,
      });
      window.alert('Invitația a fost trimisă. Elevul va primi un email pentru setarea parolei.');
      await onRefreshStudents();
    } catch (err: any) {
      setEditError(parseApiError(err));
    } finally {
      setInviteBusy(false);
    }
  };

  const resendInviteLinks = async (studentId: number) => {
    const ok = window.confirm('RetrimiÈ›i linkul de confirmare pe email (elev 16+ È™i/sau pÄƒrinÈ›i neconfirmaÈ›i)?');
    if (!ok) return;
    try {
      const resp = await api.post(`/api/classrooms/${classId}/students/${studentId}/resend-invite`, {});
      const d = (resp as any)?.data || {};
      const parts: string[] = [];
      if (d.student_sent) parts.push('elev');
      if (d.parent1_sent || d.parent2_sent) parts.push('pÄƒrinÈ›i');
      window.alert(parts.length ? `Link retrimis: ${parts.join(' + ')}` : 'Nu a fost necesarÄƒ retrimiterea (deja confirmat sau lipsÄƒ email).');
      await onRefreshStudents();
    } catch (err: any) {
      window.alert(parseApiError(err));
    }
  };

  useEffect(() => {
    if (activeTab !== 'students') return;
    if (!classId) return;

    let cancelled = false;
    const loadMetrics = async () => {
      setMetricsLoading(true);
      setMetricsError(null);
      try {
        const cfgResp = await api.get<GradebookConfigLite[]>(`/api/catalog/config/all/${classId}`);
        const cfgs = Array.isArray(cfgResp.data) ? cfgResp.data : [];
        const cfg = cfgs.slice().sort((a, b) => Number(b.id) - Number(a.id))[0] || null;
        if (!cfg) {
          if (!cancelled) setMetricsByStudentId({});
          return;
        }

        const ovResp = await api.get<CatalogOverviewLite>(`/api/catalog/classroom/${classId}/overview`, {
          params: { config_id: cfg.id, period_index: 0 },
        });
        const overview = ovResp.data;
        const educationLevel = (cfg.education_level || overview?.config?.education_level || '').toString();

        const gradesByStudent = new Map<number, CatalogGradeLite[]>();
        for (const g of overview?.grades || []) {
          if (g.is_conduct) continue;
          const arr = gradesByStudent.get(g.student_id);
          if (arr) arr.push(g);
          else gradesByStudent.set(g.student_id, [g]);
        }

        const absByStudent = new Map<number, { total: number; motivated: number }>();
        for (const a of overview?.absences || []) {
          const cur = absByStudent.get(a.student_id) || { total: 0, motivated: 0 };
          cur.total += a.total || 0;
          cur.motivated += a.motivated || 0;
          absByStudent.set(a.student_id, cur);
        }

        const result: Record<number, StudentMetric> = {};
        for (const s of students || []) {
          const sid = Number(s.id);
          const grades = gradesByStudent.get(sid) || [];
          const abs = absByStudent.get(sid) || { total: 0, motivated: 0 };

          const nums = grades
            .map((gg) => gradeToNumberForIndicator(gg, educationLevel))
            .filter((v): v is number => typeof v === 'number');
          const avg = avgOf(nums);

          const status: EvolutionStatus = (() => {
            if (avg == null) {
              if (abs.total === 0) return 'fara_date';
              if (abs.total <= 2) return 'binisor';
              return 'avertisment';
            }
            if (avg >= 8.5 && abs.total <= 2) return 'bine';
            if (avg >= 6.5 && abs.total <= 6) return 'binisor';
            return 'avertisment';
          })();

          const trend: EvolutionTrend = (() => {
            const withDate = grades
              .map((gg) => {
                const raw = (gg.date || gg.created_at || '').toString();
                if (!raw) return null;
                const d = new Date(raw);
                if (Number.isNaN(d.getTime())) return null;
                const v = gradeToNumberForIndicator(gg, educationLevel);
                if (v == null) return null;
                return { d, v };
              })
              .filter((x): x is { d: Date; v: number } => !!x)
              .sort((a, b) => a.d.getTime() - b.d.getTime());

            if (withDate.length < 2) return 'flat';
            const splitIndex = Math.floor(withDate.length / 2);
            const first = withDate.slice(0, splitIndex).map((x) => x.v);
            const second = withDate.slice(splitIndex).map((x) => x.v);
            const a1 = avgOf(first);
            const a2 = avgOf(second);
            if (a1 == null || a2 == null) return 'flat';
            const delta = a2 - a1;
            if (delta > 0.3) return 'up';
            if (delta < -0.3) return 'down';
            return 'flat';
          })();

          const base = avg != null ? (avg / 10) * 100 : abs.total > 0 ? 55 : null;
          const penalty = clamp(0, abs.total * 5, 35);
          const rating = base != null ? Math.round(clamp(0, base - penalty, 100)) : null;

          result[sid] = {
            status,
            trend,
            avg,
            absTotal: abs.total,
            absMotivated: abs.motivated,
            rating,
          };
        }

        if (!cancelled) setMetricsByStudentId(result);
      } catch (err: any) {
        if (!cancelled) setMetricsError(parseApiError(err));
      } finally {
        if (!cancelled) setMetricsLoading(false);
      }
    };

    void loadMetrics();
    return () => {
      cancelled = true;
    };
  }, [activeTab, classId, students]);

  const openAddStudent = () => {
    setFormError(null);
    setStudentForm({
      name: '',
      birthDate: '',
      manualOver16: true,
      studentEmail: '',
      studentPhone: '',
      studentAddress: '',
      parentName1: '',
      parentEmail1: '',
      parentPhone1: '',
      parentName2: '',
      parentEmail2: '',
      parentPhone2: '',
    });
    setShowAddStudentModal(true);
  };

  const submitAddStudent = async () => {
    if (!studentForm.name.trim()) {
      setFormError('Completează numele elevului.');
      return;
    }
    if (isOver16 && !studentForm.studentEmail.trim()) {
      setFormError('Completează emailul elevului.');
      return;
    }
    if (!isOver16) {
      const mamaName = studentForm.parentName1.trim();
      const mamaEmail = studentForm.parentEmail1.trim();
      const tataName = studentForm.parentName2.trim();
      const tataEmail = studentForm.parentEmail2.trim();

      if (!mamaName && !tataName) {
        setFormError('Completează numele pentru cel puțin un părinte (mamă sau tată).');
        return;
      }
      if (!mamaEmail && !tataEmail) {
        setFormError('Completează emailul pentru cel puțin un părinte (mamă sau tată).');
        return;
      }
      if (mamaEmail && !mamaName) {
        setFormError('Completează și numele mamei dacă introduci emailul mamei.');
        return;
      }
      if (tataEmail && !tataName) {
        setFormError('Completează și numele tatălui dacă introduci emailul tatălui.');
        return;
      }
    }

    const payload: StudentCreatePayload = {
      name: studentForm.name.trim(),
      birth_date: studentForm.birthDate ? studentForm.birthDate : null,
      over16: isOver16,

      student_email: isOver16 ? studentForm.studentEmail.trim() : null,
      student_phone: studentForm.studentPhone.trim() || null,
      student_address: studentForm.studentAddress.trim() || null,

      parent_name1: studentForm.parentName1.trim() || null,
      parent_email1: studentForm.parentEmail1.trim() || null,
      parent_phone1: studentForm.parentPhone1.trim() || null,

      parent_name2: studentForm.parentName2.trim() || null,
      parent_email2: studentForm.parentEmail2.trim() || null,
      parent_phone2: studentForm.parentPhone2.trim() || null,
    };

    setBusy(true);
    setFormError(null);
    try {
      await onAddStudent(payload);
      setShowAddStudentModal(false);
      await onRefreshStudents();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <button className="p-2 rounded-md hover:bg-muted transition-colors duration-150" onClick={onBack} aria-label="Înapoi">
            <Icon name="ChevronLeft" size={20} className="text-muted-foreground" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground truncate">{className}</h1>
            <p className="text-sm text-muted-foreground">Profesor: {profesor}</p>
          </div>
        </div>

        {isAdmin && (
          <div className="hidden sm:flex items-center gap-2">
            {!isB2B && (
              <Button variant="outline" iconName="Pencil" iconPosition="left" onClick={onRename}>
                Redenumește
              </Button>
            )}
            <Button variant="outline" iconName="UserCog" iconPosition="left" onClick={onAddAdmin}>
              Admini
            </Button>
            {!isB2B && (
              <Button variant="destructive" iconName="Trash2" iconPosition="left" onClick={onDeleteClass}>
                Șterge
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="mb-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={activeTab === 'catalog' ? 'default' : 'outline'}
            iconName="BookOpen"
            iconPosition="left"
            onClick={() => onTabChange('catalog')}
          >
            Catalog
          </Button>
          <Button
            variant={activeTab === 'teme' ? 'default' : 'outline'}
            iconName="FileText"
            iconPosition="left"
            onClick={() => onTabChange('teme')}
          >
            Teme
          </Button>
          <Button
            variant={activeTab === 'cursuri' ? 'default' : 'outline'}
            iconName="BookOpen"
            iconPosition="left"
            onClick={() => onTabChange('cursuri')}
          >
            Cursuri
          </Button>
          <Button
            variant={activeTab === 'orar' ? 'default' : 'outline'}
            iconName="CalendarDays"
            iconPosition="left"
            onClick={() => onTabChange('orar')}
          >
            Orar
          </Button>
          <Button
            variant={activeTab === 'students' ? 'default' : 'outline'}
            iconName="Users"
            iconPosition="left"
            onClick={() => onTabChange('students')}
          >
            Elevi
          </Button>
          <Button
            variant={activeTab === 'profesori' ? 'default' : 'outline'}
            iconName="GraduationCap"
            iconPosition="left"
            onClick={() => onTabChange('profesori')}
          >
            Profesori
          </Button>
        </div>

        {isAdmin && !isB2B && activeTab === 'students' && (
          <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={openAddStudent}>
            Adaugă elev
          </Button>
        )}
      </div>

      {info && (
        <div className="mb-4 bg-success/10 border border-success/20 text-success rounded-md p-3 text-sm">{info}</div>
      )}
      {studentsError && (
        <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{studentsError}</div>
      )}

      {activeTab === 'catalog' && <CatalogPanel classroomId={classId} />}
      {activeTab === 'teme' && <TemePanel classroomId={classId} />}
      {activeTab === 'cursuri' && <CursuriPanel classroomId={classId} />}
      {activeTab === 'orar' && <OrarPanel classId={classId} />}
      {activeTab === 'profesori' && <ProfesoriPanel classroomId={classId} />}
      {activeTab === 'students' && (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-foreground">Elevi</h2>
          {isAdmin && !isB2B && (
            <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={openAddStudent}>
              Adaugă elev
            </Button>
          )}
        </div>
        {isB2B && (
          <div className="mb-4 text-sm bg-muted border border-border rounded-md p-3 text-foreground">
            Mod B2B: elevii sunt gestionați de secretariat.
          </div>
        )}

        {metricsError && (
          <div className="mb-3 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{metricsError}</div>
        )}

        {studentsLoading ? (
          <div className="text-sm text-muted-foreground">Se încarcă elevii…</div>
        ) : students.length === 0 ? (
          <div className="text-sm text-muted-foreground">Nu există elevi în clasa selectată.</div>
        ) : (
          <div className="divide-y divide-border">
            {students.map((s) => {
              const isOpen = openStudentId === s.id;
              const m = metricsByStudentId[Number(s.id)];
              const birthDateLabel = formatDateOnlyRo(s.date_of_birth);
              const studentOver16 =
                typeof (s as any).over16 === 'boolean'
                  ? Boolean((s as any).over16)
                  : calculateOver16(s.date_of_birth ? String(s.date_of_birth) : null, false);

              const statusUi =
                !m
                  ? { label: metricsLoading ? 'Se calculeaza...' : 'Fara date', badge: 'bg-muted/40 border-border text-muted-foreground' }
                  : m.status === 'bine'
                  ? { label: 'Bine', badge: 'bg-success/10 border-success/30 text-success' }
                  : m.status === 'binisor'
                  ? { label: 'Binisor', badge: 'bg-warning/10 border-warning/30 text-warning' }
                  : m.status === 'avertisment'
                  ? { label: 'Avertisment', badge: 'bg-error/10 border-error/30 text-error' }
                  : { label: 'Fara date', badge: 'bg-muted/40 border-border text-muted-foreground' };

              const trendIcon = !m ? 'Minus' : m.trend === 'up' ? 'TrendingUp' : m.trend === 'down' ? 'TrendingDown' : 'Minus';
              const trendColor =
                !m ? 'text-muted-foreground' : m.trend === 'up' ? 'text-success' : m.trend === 'down' ? 'text-error' : 'text-muted-foreground';

              const rating = m?.rating ?? null;
              const ratingColor = rating == null ? 'bg-muted' : rating >= 80 ? 'bg-success' : rating >= 60 ? 'bg-warning' : 'bg-error';
              const ratingText = rating == null ? '—' : `${rating}%`;

              const detail = m
                ? [m.avg != null ? `Medie: ${m.avg}` : null, `Absente: ${m.absTotal}` + (m.absMotivated ? ` (motivate: ${m.absMotivated})` : '')]
                    .filter(Boolean)
                    .join(' · ')
                : '';

              return (
                <div key={s.id} className="py-2">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-3">
                    <button
                      className="flex-1 min-w-0 text-left rounded-md hover:bg-muted/40 transition-colors duration-150 px-2 py-2"
                      onClick={() => setOpenStudentId((prev) => (prev === s.id ? null : s.id))}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{s.full_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{s.email}</p>
                          <p className="text-xs text-muted-foreground truncate">Data nasterii: {birthDateLabel || '—'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            title={detail || undefined}
                            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusUi.badge}`}
                          >
                            {statusUi.label}
                          </span>
                          <Icon name={trendIcon} size={16} className={trendColor} />
                          <div className="hidden md:flex items-center gap-2 min-w-[160px]">
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full ${ratingColor}`} style={{ width: `${rating ?? 0}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground min-w-[42px] text-right">{ratingText}</span>
                          </div>
                          <Icon
                            name="ChevronDown"
                            size={18}
                            className={`text-muted-foreground transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </div>
                      {detail && <div className="mt-1 text-xs text-muted-foreground truncate">{detail}</div>}
                    </button>

                    <div className="flex items-center justify-end gap-1 w-full sm:w-auto sm:mt-1">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="MessageSquare"
                        iconPosition="left"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToDm(Number(s.id), s.full_name, 'student');
                        }}
                        className="w-full sm:w-auto"
                      >
                        Mesaj
                      </Button>

                      {isAdmin && !isB2B ? (
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Send"
                          iconPosition="left"
                          onClick={(e) => {
                            e.stopPropagation();
                            void resendInviteLinks(Number(s.id));
                          }}
                          className="w-full sm:w-auto"
                        >
                          Retrimite
                        </Button>
                      ) : null}

                      {(isAdmin || canEditStudents) && (
                        <>
                        {canEditStudents ? (
                          <button
                            className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditStudent(s);
                            }}
                            aria-label="Editeaza elevul"
                            type="button"
                          >
                            <Icon name="Pencil" size={18} className="text-muted-foreground" />
                          </button>
                        ) : null}
                        {!isB2B ? (
                          <button
                            className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                            onClick={(e) => {
                              e.stopPropagation();
                              void onDeleteStudent(s.id);
                            }}
                            aria-label="Sterge elevul"
                            type="button"
                          >
                            <Icon name="Trash2" size={18} className="text-error" />
                          </button>
                        ) : null}
                        </>
                      )}
                    </div>
                  </div>

                  {isOpen && (
                    <div className="mt-2 px-2 pb-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-muted/20 border border-border rounded-lg p-4">
                        <div>
                          <div className="text-xs text-muted-foreground">Telefon</div>
                          <div className="text-sm text-foreground">{s.phone || '—'}</div>
                          <div className="text-xs text-muted-foreground mt-2">Data nasterii</div>
                          <div className="text-sm text-foreground">{birthDateLabel || '—'}</div>
                        </div>
                        <div className="md:col-span-2">
                          <div className="text-xs text-muted-foreground">Adresa</div>
                          <div className="text-sm text-foreground">{s.address || '—'}</div>
                        </div>

                        <div className="md:col-span-3">
                          <div className="text-xs text-muted-foreground">Email elev (16+)</div>
                          <button
                            type="button"
                            className={`mt-1 w-full text-left px-3 py-2 rounded-md border border-border transition-colors duration-150 ${
                              studentOver16 && canEditStudents
                                ? 'bg-background hover:bg-muted/40 text-foreground'
                                : 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                            }`}
                            onClick={() => {
                              if (!studentOver16 || !canEditStudents) return;
                              openEditStudent(s);
                            }}
                            disabled={!studentOver16 || !canEditStudents}
                            title={studentOver16 ? 'Click pentru editare + trimitere activare' : 'Disponibil după 16 ani'}
                          >
                            {studentOver16 ? (s.email || 'â€”') : 'Disponibil după 16 ani'}
                          </button>
                          {!studentOver16 ? (
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              Câmpul este vizibil, dar se poate completa doar după împlinirea vârstei de 16 ani.
                            </div>
                          ) : null}
                        </div>

                        <div className="md:col-span-3 border-t border-border pt-3">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="text-sm font-medium text-foreground">Evolutie si rating</div>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${statusUi.badge}`}>
                                {statusUi.label}
                              </span>
                              <Icon name={trendIcon} size={16} className={trendColor} />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div className="bg-background border border-border rounded-md p-3">
                              <div className="text-xs text-muted-foreground">Medie</div>
                              <div className="text-sm font-medium text-foreground">{m?.avg != null ? m.avg : '—'}</div>
                            </div>
                            <div className="bg-background border border-border rounded-md p-3">
                              <div className="text-xs text-muted-foreground">Absente</div>
                              <div className="text-sm font-medium text-foreground">
                                {m ? `${m.absTotal}${m.absMotivated ? ` (motivate: ${m.absMotivated})` : ''}` : '—'}
                              </div>
                            </div>
                            <div className="bg-background border border-border rounded-md p-3">
                              <div className="text-xs text-muted-foreground">Rating</div>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                  <div className={`h-full ${ratingColor}`} style={{ width: `${rating ?? 0}%` }} />
                                </div>
                                <div className="text-sm font-medium text-foreground min-w-[48px] text-right">{ratingText}</div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="md:col-span-3 border-t border-border pt-3">
                          <div className="text-sm font-medium text-foreground mb-2">Parinti</div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="bg-background border border-border rounded-md p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="text-xs text-muted-foreground mb-1">Părinte (mamă)</div>
                                {s.parent_user_id1 ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="MessageSquare"
                                    iconPosition="left"
                                    onClick={() => goToDm(Number(s.parent_user_id1), s.parent_name1 || 'Părinte', 'parent')}
                                  >
                                    Mesaj
                                  </Button>
                                ) : null}
                              </div>
                              <div className="text-sm text-foreground">{s.parent_name1 || '—'}</div>
                              <div className="text-xs text-muted-foreground mt-1">{s.parent_email1 || '—'}</div>
                              <div className="text-xs text-muted-foreground">{s.parent_phone1 || '—'}</div>
                            </div>
                            <div className="bg-background border border-border rounded-md p-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="text-xs text-muted-foreground mb-1">Părinte (tată)</div>
                                {s.parent_user_id2 ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    iconName="MessageSquare"
                                    iconPosition="left"
                                    onClick={() => goToDm(Number(s.parent_user_id2), s.parent_name2 || 'Părinte', 'parent')}
                                  >
                                    Mesaj
                                  </Button>
                                ) : null}
                              </div>
                              <div className="text-sm text-foreground">{s.parent_name2 || '—'}</div>
                              <div className="text-xs text-muted-foreground mt-1">{s.parent_email2 || '—'}</div>
                              <div className="text-xs text-muted-foreground">{s.parent_phone2 || '—'}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {showAddStudentModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="text-lg font-semibold text-foreground">Adaugă elev</h3>
              <button
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                onClick={() => setShowAddStudentModal(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {formError && (
                <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{formError}</div>
              )}

              <Input label="Nume elev *" value={studentForm.name} onChange={(e) => setStudentForm((p) => ({ ...p, name: e.target.value }))} />
              <Input label="Data nașterii (opțional)" type="date" value={studentForm.birthDate} onChange={(e) => setStudentForm((p) => ({ ...p, birthDate: e.target.value }))} />

              {!studentForm.birthDate && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={studentForm.manualOver16}
                    onChange={(e) => setStudentForm((p) => ({ ...p, manualOver16: e.target.checked }))}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Elevul are cel puțin 16 ani</span>
                </label>
              )}

              {isOver16 ? (
                <>
                  <Input label="Email elev *" type="email" value={studentForm.studentEmail} onChange={(e) => setStudentForm((p) => ({ ...p, studentEmail: e.target.value }))} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input label="Telefon elev (opțional)" value={studentForm.studentPhone} onChange={(e) => setStudentForm((p) => ({ ...p, studentPhone: e.target.value }))} />
                    <Input label="Adresă reședință (opțional)" value={studentForm.studentAddress} onChange={(e) => setStudentForm((p) => ({ ...p, studentAddress: e.target.value }))} />
                  </div>

                  <div className="border-t border-border pt-3">
                    <div className="text-sm font-medium text-foreground mb-1">Părinți (opțional)</div>
                    <div className="text-xs text-muted-foreground mb-3">
                      Dacă adaugi emailul părintelui, se creează un cont de părinte pentru vizualizarea evoluției elevului.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <Input label="Nume părinte (mamă) (opțional)" value={studentForm.parentName1} onChange={(e) => setStudentForm((p) => ({ ...p, parentName1: e.target.value }))} />
                        <Input label="Email părinte (mamă) (opțional)" type="email" value={studentForm.parentEmail1} onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail1: e.target.value }))} />
                        <Input label="Telefon părinte (mamă) (opțional)" value={studentForm.parentPhone1} onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone1: e.target.value }))} />
                      </div>
                      <div className="space-y-3">
                        <Input label="Nume părinte (tată) (opțional)" value={studentForm.parentName2} onChange={(e) => setStudentForm((p) => ({ ...p, parentName2: e.target.value }))} />
                        <Input label="Email părinte (tată) (opțional)" type="email" value={studentForm.parentEmail2} onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail2: e.target.value }))} />
                        <Input label="Telefon părinte (tată) (opțional)" value={studentForm.parentPhone2} onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone2: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Completează numele și emailul pentru cel puțin un părinte (mamă sau tată). Se creează automat un cont de părinte pentru elevii sub 16 ani.
                  </p>
	                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
	                    <div className="space-y-3">
	                      <Input label="Nume părinte (mamă)" value={studentForm.parentName1} onChange={(e) => setStudentForm((p) => ({ ...p, parentName1: e.target.value }))} />
	                      <Input label="Email părinte (mamă)" type="email" value={studentForm.parentEmail1} onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail1: e.target.value }))} />
	                      <Input label="Telefon părinte (mamă) (opțional)" value={studentForm.parentPhone1} onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone1: e.target.value }))} />
	                    </div>
	                    <div className="space-y-3">
	                      <Input label="Nume părinte (tată)" value={studentForm.parentName2} onChange={(e) => setStudentForm((p) => ({ ...p, parentName2: e.target.value }))} />
	                      <Input label="Email părinte (tată)" type="email" value={studentForm.parentEmail2} onChange={(e) => setStudentForm((p) => ({ ...p, parentEmail2: e.target.value }))} />
	                      <Input label="Telefon părinte (tată) (opțional)" value={studentForm.parentPhone2} onChange={(e) => setStudentForm((p) => ({ ...p, parentPhone2: e.target.value }))} />
	                    </div>
	                    <div className="md:col-span-2">
	                      <Input label="Adresă reședință (opțional)" value={studentForm.studentAddress} onChange={(e) => setStudentForm((p) => ({ ...p, studentAddress: e.target.value }))} />
	                    </div>
	                  </div>
                </>
              )}

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAddStudentModal(false)}>
                  Anulează
                </Button>
                <Button variant="default" onClick={submitAddStudent} disabled={busy}>
                  Salvează
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditStudentModal && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="p-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="text-lg font-semibold text-foreground">Editeaza elev</h3>
              <button
                className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
                onClick={() => setShowEditStudentModal(false)}
                aria-label="Inchide"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {editError && <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{editError}</div>}

              <Input label="Nume elev *" value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
              <Input label="Data nasterii (optional)" type="date" value={editForm.birthDate} onChange={(e) => setEditForm((p) => ({ ...p, birthDate: e.target.value }))} />

              {!editForm.birthDate && (
                <label className="flex items-center gap-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={editForm.manualOver16}
                    onChange={(e) => setEditForm((p) => ({ ...p, manualOver16: e.target.checked }))}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                  />
                  <span>Elevul are cel putin 16 ani</span>
                </label>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Email elev (pentru elevi >=16)"
                  type="email"
                  value={isEditOver16 ? editForm.studentEmail : ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, studentEmail: e.target.value }))}
                  disabled={!isEditOver16}
                  placeholder={!isEditOver16 ? 'Elev sub 16 ani (email intern)' : undefined}
                />
                <Input label="Telefon elev (optional)" value={editForm.studentPhone} onChange={(e) => setEditForm((p) => ({ ...p, studentPhone: e.target.value }))} />
                <div className="md:col-span-2">
                  <Input label="Adresa resedinta (optional)" value={editForm.studentAddress} onChange={(e) => setEditForm((p) => ({ ...p, studentAddress: e.target.value }))} />
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="text-xs text-muted-foreground">
                  Pentru elevii 16+, poți trimite un email cu link de setare parolă (cont self-managed).
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={inviteOver16Student}
                  disabled={!isEditOver16 || inviteBusy || !(editForm.studentEmail || '').trim()}
                  loading={inviteBusy}
                >
                  Trimite activare
                </Button>
              </div>

              <div className="border-t border-border pt-4">
                <div className="text-sm font-medium text-foreground mb-3">Parinti</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Input label="Nume părinte (mamă)" value={editForm.parentName1} onChange={(e) => setEditForm((p) => ({ ...p, parentName1: e.target.value }))} />
                    <Input label="Email părinte (mamă)" type="email" value={editForm.parentEmail1} onChange={(e) => setEditForm((p) => ({ ...p, parentEmail1: e.target.value }))} />
                    <Input label="Telefon părinte (mamă)" value={editForm.parentPhone1} onChange={(e) => setEditForm((p) => ({ ...p, parentPhone1: e.target.value }))} />
                  </div>
                  <div className="space-y-3">
                    <Input label="Nume părinte (tată)" value={editForm.parentName2} onChange={(e) => setEditForm((p) => ({ ...p, parentName2: e.target.value }))} />
                    <Input label="Email părinte (tată)" type="email" value={editForm.parentEmail2} onChange={(e) => setEditForm((p) => ({ ...p, parentEmail2: e.target.value }))} />
                    <Input label="Telefon părinte (tată)" value={editForm.parentPhone2} onChange={(e) => setEditForm((p) => ({ ...p, parentPhone2: e.target.value }))} />
                  </div>
                </div>
                <div className="text-xs text-muted-foreground mt-3">
                  Daca completezi emailul parintelui, se creeaza automat un cont de parinte (daca nu exista), indiferent de varsta elevului.
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowEditStudentModal(false)}>
                  Anuleaza
                </Button>
                <Button variant="default" onClick={submitEditStudent} disabled={editBusy}>
                  Salveaza
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClassroomDetailView;
